// Presentation helpers for the platform health page (ADR-198 DRAFT; G001 Lane AR DESIGN D6).
//
// THE PAGE EVALUATES NOTHING. Every state, verdict, colour, role, status line and the headline arrive from
// GET /api/health, computed server-side by the port of the golden-vector rule. `look()` below is the ONLY place a
// server state becomes a style, and it can only DOWNGRADE: an answer whose own fields disagree (MEASURED OK with no
// measurement time, OK with a non-green colour, an unrecognised state or verdict, an asset "fine" that no check lands
// on, a status line "ok" that cites no check) renders UNKNOWN with the contradiction spelled out. There is no path to
// the "fine" style without MEASURED + OK + green + a parseable measurement time. RECOVERED is its own kind: never fine,
// never a problem. Unmeasured is never healthy.
import {
  GRID_ENVS,
  type HealthCoverage,
  type HealthGridAsset,
  type HealthGridLine,
  type HealthMonitor,
  type HealthResponse,
  type HealthTile
} from "./types";

export const POLL_MS = 5 * 60_000;
export const FETCH_TIMEOUT_MS = 30_000;
/** Older than three missed polls: the answer on screen is no longer current and the page says so. */
export const STALE_AFTER_MS = 15 * 60_000;
/** The same tolerance as the rule's FUTURE_TOLERANCE_H (5 minutes). */
export const SKEW_TOLERANCE_MS = 5 * 60_000;

/** Six visual kinds, each carried by a WORD and a PATTERN, never by colour alone:
 *  problem = solid fill · unknown = hatched + the literal word UNKNOWN · fine = dot · na = dashed outline ·
 *  info = dotted border · recovered = the info style + the word RECOVERED */
export type Kind = "problem" | "unknown" | "recovered" | "fine" | "na" | "info";
export const KIND_RANK: Record<Kind, number> = { problem: 0, unknown: 1, recovered: 2, info: 3, na: 4, fine: 5 };
export const NO_KINDS = (): Record<Kind, number> => ({ problem: 0, unknown: 0, recovered: 0, info: 0, na: 0, fine: 0 });

export interface Look {
  kind: Kind;
  word: string;
  /** Set when the server's own fields disagree; the page then shows the worse reading and says why. */
  contradiction: string | null;
}

export interface StateFields {
  state: string;
  verdict: string | null;
  colour: string;
  measured_at_utc: string | null;
}

export function isTime(s: unknown): s is string {
  return typeof s === "string" && s.trim() !== "" && !Number.isNaN(Date.parse(s));
}

export function clip(s: string, n: number): string {
  if (s.length <= n) return s;
  let end = Math.max(0, n - 1);
  const c = s.charCodeAt(end - 1);
  if (end > 0 && c >= 0xd800 && c <= 0xdbff) end -= 1;      // never split a surrogate pair (an emoji at the cut)
  return `${s.slice(0, end)}…`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export const UNKNOWN_LOOK: Look = { kind: "unknown", word: "UNKNOWN", contradiction: null };

function unknownLook(contradiction: string | null): Look {
  return { kind: "unknown", word: "UNKNOWN", contradiction };
}

/** THE ONLY place a server state becomes a style. It maps; it never upgrades. */
export function look(x: StateFields): Look {
  if (x.state === "UNKNOWN") return unknownLook(null);
  if (x.state === "NOT_APPLICABLE") {
    // NOT_APPLICABLE carries no verdict and a grey colour; anything else contradicts itself (DESIGN-R2 B2) and is never
    // drawn as a quiet "not applicable" - a BAD verdict or red colour beside it must not be softened away.
    if (x.verdict !== null || x.colour !== "grey") {
      return unknownLook(
        `the server sent NOT_APPLICABLE with verdict '${clip(String(x.verdict), 20)}' and colour '${clip(String(x.colour), 20)}' - state and verdict disagree, so it is shown as UNKNOWN`
      );
    }
    return { kind: "na", word: "NOT APPLICABLE", contradiction: null };
  }
  if (x.state !== "MEASURED") {
    return unknownLook(`the server sent an unrecognised state '${clip(String(x.state), 40)}' - shown as UNKNOWN`);
  }
  // A measured problem stays a problem whatever else is missing: the page never softens a BAD.
  if (x.verdict === "BAD") return { kind: "problem", word: "MEASURED · PROBLEM", contradiction: null };
  // Recovered: failed within its window and succeeded since (rule 18 / A5). Never fine, never a problem.
  if (x.verdict === "RECOVERED") return { kind: "recovered", word: "MEASURED · RECOVERED", contradiction: null };
  if (x.verdict === "INFO") return { kind: "info", word: "MEASURED · INFO", contradiction: null };
  if (x.verdict !== "OK") {
    return unknownLook("the server sent MEASURED with no recognised verdict - shown as UNKNOWN");
  }
  if (!isTime(x.measured_at_utc)) {
    return unknownLook("the server sent MEASURED OK with no measurement time - unmeasured is never shown as healthy");
  }
  if (x.colour !== "green") {
    return unknownLook(
      `the server sent MEASURED OK with colour '${clip(String(x.colour), 20)}' - state and colour disagree, so it is not shown as healthy`
    );
  }
  return { kind: "fine", word: "MEASURED · OK", contradiction: null };
}

/** An asset's look: look(), and an asset NOTHING lands on - no check and no per-pipeline evidence (DESIGN-R3 C6) - is
 *  never drawn fine or recovered (grid rule 3); a server answer that says so contradicts itself and is shown UNKNOWN, with
 *  the contradiction named. Evidence counts as a landing for that test, but evidence is never OK: an asset drawn fine
 *  carries at least one landed check and no evidence. */
export function assetLook(a: StateFields & { tiles: readonly unknown[]; evidence?: readonly unknown[] }): Look {
  const lk = look(a);
  const evidence = Array.isArray(a.evidence) ? a.evidence : [];
  if ((lk.kind === "fine" || lk.kind === "recovered") && a.tiles.length + evidence.length === 0) {
    return unknownLook(
      `the server sent ${lk.kind === "fine" ? "MEASURED OK" : "MEASURED RECOVERED"} for an asset no check lands on - unmeasured is never shown as healthy`
    );
  }
  // per-pipeline evidence is only ever a problem, a recovery or a run that cannot be read (DESIGN-R2 A2, DESIGN-R3 C2):
  // a "fine" answer beside any of it, or with no landed check of its own, contradicts itself
  if (lk.kind === "fine" && (a.tiles.length === 0 || evidence.length > 0)) {
    return unknownLook(
      `the server sent MEASURED OK for an asset whose per-pipeline evidence (${plural(evidence.length, "reading")}: overdue, recovered or not observable) says otherwise - it is not shown as healthy`
    );
  }
  // A quiet asset (NOT APPLICABLE / INFO) never carries a measured problem, a RECOVERED or an UNKNOWN reading without
  // saying so (DESIGN-R3 C6: RECOVERED is loud too): the API moves such a check to grid.unplaced (DESIGN-R2 A4, R3 C4);
  // an answer that did not - a landed check or an evidence entry - is shown UNKNOWN, the contradiction named.
  if (lk.kind === "na" || lk.kind === "info") {
    const loudTiles = a.tiles.map(t => (isStateFields(t) ? look(t).kind : "unknown")).filter(k => k === "problem" || k === "unknown" || k === "recovered");
    const loud = loudTiles.length + evidence.length;
    if (loud > 0) {
      return unknownLook(
        `the server sent ${lk.kind === "na" ? "NOT_APPLICABLE" : "MEASURED INFO"} for an asset on which ${plural(loud, "measured problem, RECOVERED or UNKNOWN reading")} landed - it is not shown as quiet`
      );
    }
  }
  return lk;
}

function isStateFields(x: unknown): x is StateFields {
  return typeof x === "object" && x !== null && typeof (x as StateFields).state === "string";
}

/** The short state word a tile face carries (look().word is the long form, used in the detail panel). */
export const SHORT_WORD: Record<Kind, string> = {
  problem: "PROBLEM",
  unknown: "UNKNOWN",
  recovered: "RECOVERED",
  fine: "OK",
  na: "NOT APPLICABLE",
  info: "INFO"
};

export type LineKind = HealthGridLine["kind"];
const LINE_KINDS: readonly string[] = ["ok", "problem", "recovered", "unknown", "na", "none"];

export interface LineLook {
  kind: LineKind;
  word: string;
  contradiction: string | null;
}

/** A status line as drawn. It maps the server's word and kind; an unrecognised kind, or an "ok" / "recovered" line
 *  that cites no check (grid rule 4: never ok without a MEASURED cited check), is drawn UNKNOWN and says why. */
export function lineLook(l: HealthGridLine | null | undefined): LineLook {
  if (!l || typeof l.word !== "string" || !LINE_KINDS.includes(String(l.kind))) {
    return { kind: "unknown", word: "UNKNOWN", contradiction: "the server sent a status line this page does not recognise - shown as UNKNOWN" };
  }
  if ((l.kind === "ok" || l.kind === "recovered") && (!Array.isArray(l.cites) || l.cites.length === 0)) {
    return {
      kind: "unknown",
      word: "UNKNOWN",
      contradiction: `the server marked this line '${clip(l.word, 30)}' but cites no check - a line is never ok without a measured check`
    };
  }
  return { kind: l.kind, word: l.word.trim() === "" ? "UNKNOWN" : l.word, contradiction: null };
}

/** The reason a status line carries on the tile face as a title / accessible description (DESIGN-R2 B6) - never as
 *  visible text. A line the page downgraded says why; a line with no reason gives none (never an invented one). */
export function lineReason(l: HealthGridLine | null | undefined): string | null {
  const lk = lineLook(l);
  const own = l && typeof l.reason === "string" ? l.reason.trim() : "";
  if (lk.contradiction) return own ? `${lk.contradiction} (the server's reason: ${own})` : lk.contradiction;
  return own !== "" ? own : null;
}

/** A recorded control-plane summary's tag on the tile face (DESIGN-R2 B5): "measured <date>" or "INHERITED <date>";
 *  any other tag is shown as it is, flagged - a claim is MEASURED or INHERITED, never silently one of them. */
export interface TagLook {
  kind: "measured" | "inherited" | "other";
  text: string;
}
export function summaryTag(tag: string | null | undefined, at: string | null | undefined): TagLook {
  const t = typeof tag === "string" ? tag.trim() : "";
  const date = recordedDate(at);
  if (t.toUpperCase() === "MEASURED") return { kind: "measured", text: `measured ${date}` };
  if (t.toUpperCase() === "INHERITED") return { kind: "inherited", text: `INHERITED ${date}` };
  return { kind: "other", text: `${t === "" ? "untagged" : clip(t, 20)} (not MEASURED or INHERITED) ${date}` };
}

/** A label split where a line may break (DESIGN-R2 B16): after "_" and at a camel-case hump ("SnapshotSelector" ->
 *  "Snapshot" + "Selector"). The pieces join back to the label exactly; the page puts a <wbr> between them. */
export function labelParts(label: string): string[] {
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < label.length; i++) {
    const c = label[i];
    const prev = i > 0 ? label[i - 1] : "";
    if (cur !== "" && /[A-Z]/.test(c) && /[a-z]/.test(prev)) {
      out.push(cur);
      cur = "";
    }
    cur += c;
    if (c === "_" && i < label.length - 1) {
      out.push(cur);
      cur = "";
    }
  }
  if (cur !== "") out.push(cur);
  return out;
}

/** The assets the band collapsed under a primary (role consequent) - the "Downstream" count's entries (DESIGN-R2 B1). */
export function downstreamEntries(r: HealthResponse): string[] {
  return r.grid.assets.filter(a => !a.hidden && a.role === "consequent").map(a => a.id);
}

/** The reconciliation line (DESIGN-R2 B3, DESIGN-R3 C7 / C8): how the rows read became what is drawn, from
 *  grid.reconciliation, with every disagreement - the answer's own `balanced`, its arithmetic, and its numbers against
 *  what the answer actually lists - named as a contradiction. An answer with no reconciliation cannot be reconciled: that
 *  is said, as UNKNOWN. Line 2 never says "problem" beside the Problems chip: the measured-BAD entries are named as such
 *  and split the way the counts line counts them. */
export interface ReconciliationView {
  known: boolean;
  lines: string[];
  contradictions: string[];
}

/** DESIGN-R3 C7: the visible MEASURED/BAD entries split the way the counts line counts them - computed from the assets'
 *  own section and role: a consequent (in any section) is Downstream; otherwise housekeeping is Housekeeping, monitoring
 *  is Control plane and anything else is a data Problem. A synthetic entry (the unplaced checks) has no section on the
 *  page: it is placed by the band list that names it (primaries / possibly consequent = Problems, monitoring = Control
 *  plane); one named in neither is not measured BAD. Decides no state - it counts the server's. */
export interface BadSplit {
  problems: number;
  downstream: number;
  control: number;
  housekeeping: number;
  total: number;
}
export function measuredBadSplit(r: HealthResponse): BadSplit {
  const s: BadSplit = { problems: 0, downstream: 0, control: 0, housekeeping: 0, total: 0 };
  for (const a of r.grid.assets) {
    if (a.hidden || a.state !== "MEASURED" || a.verdict !== "BAD") continue;
    if (a.role === "consequent") s.downstream += 1;
    else if (a.section === "housekeeping") s.housekeeping += 1;
    else if (a.section === "monitoring") s.control += 1;
    else s.problems += 1;
  }
  const b = r.band;
  for (const id of r.grid.reconciliation?.synthetic_entries ?? []) {
    if (b.primaries.includes(id) || b.possibly_consequent.includes(id)) s.problems += 1;
    else if (b.monitoring.includes(id)) s.control += 1;
  }
  s.total = s.problems + s.downstream + s.control + s.housekeeping;
  return s;
}

const INVALID_ASSET = "invalid:";

/** DESIGN-R3 C5: how the answer's synthetic flow entries disagree with its own lists. "unplaced" is there exactly when a
 *  check landed on no asset; nothing twice. When the answer names, on each grid.invalid entry, the flow entry it counts
 *  under (`entry`, the API's DESIGN-R3 C5), the invalid-family synthetic entries are EXACTLY the distinct entries named -
 *  each an "invalid" / "invalid:..." id, every note naming one. An answer older than R3 (no `entry`): an invalid-family
 *  entry is there exactly when grid.invalid is not empty; each "invalid:<id>" names an id grid.invalid lists; an invalid
 *  entry with no id needs "invalid"; an invalid entry naming an id that is no drawn asset (an asset the API could not
 *  place) needs its "invalid:<id>" or the one "invalid"; nothing else. */
export function syntheticProblems(r: HealthResponse): string[] {
  const g = r.grid;
  const syn = g.reconciliation?.synthetic_entries ?? [];
  const out: string[] = [];
  const say = (s: string) => clip(s, 40);
  const dup = syn.filter((s, i) => syn.indexOf(s) !== i);
  if (dup.length > 0) out.push(`the reconciliation names the synthetic entr${dup.length === 1 ? "y" : "ies"} [${[...new Set(dup)].map(say).join(", ")}] more than once`);
  const hasUnplaced = syn.includes("unplaced");
  if (hasUnplaced !== g.unplaced.length > 0) {
    out.push(g.unplaced.length > 0
      ? `${plural(g.unplaced.length, "check")} landed on no asset, but the reconciliation counts no unplaced-checks entry`
      : "the reconciliation counts an unplaced-checks entry, but no check landed on no asset");
  }
  const named = g.invalid.map(x => x.entry).filter((e): e is string => typeof e === "string");
  if (named.length > 0) {
    // the answer says which entry each invalid grid entry counts under: follow it exactly
    if (named.length !== g.invalid.length) out.push("some invalid grid entries name the entry they count under and some do not");
    const notInvalid = [...new Set(named.filter(e => e !== "invalid" && !e.startsWith(INVALID_ASSET)))];
    if (notInvalid.length > 0) out.push(`an invalid grid entry counts under [${notInvalid.map(say).join(", ")}], which is not an invalid-entries entry`);
    const want = new Set(named);
    const odd = syn.filter(s => s !== "unplaced" && !want.has(s));
    if (odd.length > 0) out.push(`the reconciliation counts synthetic entries the answer's own lists do not call for: [${odd.map(say).join(", ")}]`);
    const missing = [...want].filter(e => !syn.includes(e));
    if (missing.length > 0) out.push(`the answer's invalid grid entries count under [${missing.map(say).join(", ")}], but the reconciliation counts no such entry`);
    return out;
  }
  const invalidIds = new Set(g.invalid.map(x => x.id).filter((x): x is string => typeof x === "string"));
  const drawn = new Set(g.assets.map(a => a.id));
  const odd = syn.filter(s => s !== "unplaced" && s !== "invalid" && !(s.startsWith(INVALID_ASSET) && invalidIds.has(s.slice(INVALID_ASSET.length))));
  if (odd.length > 0) out.push(`the reconciliation counts synthetic entries the answer's own lists do not call for: [${odd.map(say).join(", ")}]`);
  const invalidFamily = syn.filter(s => s === "invalid" || s.startsWith(INVALID_ASSET));
  if ((invalidFamily.length > 0) !== g.invalid.length > 0) {
    out.push(g.invalid.length > 0
      ? `the answer lists ${g.invalid.length} invalid grid entr${g.invalid.length === 1 ? "y" : "ies"}, but the reconciliation counts no entry for ${g.invalid.length === 1 ? "it" : "them"}`
      : "the reconciliation counts an invalid-entries entry, but the answer lists no invalid grid entry");
  } else if (g.invalid.length > 0) {
    const generic = syn.includes("invalid");
    if (g.invalid.some(x => x.id === null) && !generic) out.push("an invalid grid entry names no asset, but the reconciliation counts no 'invalid' entry for it");
    const uncovered = [...invalidIds].filter(id => !drawn.has(id) && !generic && !syn.includes(`${INVALID_ASSET}${id}`));
    if (uncovered.length > 0) out.push(`the API could not place [${uncovered.map(say).join(", ")}], but the reconciliation counts no entry for ${uncovered.length === 1 ? "it" : "them"}`);
  }
  return out;
}

/** The synthetic entries as line 2 names them. */
function syntheticWords(syn: readonly string[]): string[] {
  const words: string[] = [];
  const perAsset = syn.filter(s => s.startsWith(INVALID_ASSET));
  for (const s of syn) {
    if (s === "unplaced") words.push("the unplaced-checks entry");
    else if (s === "invalid") words.push("the invalid-entries entry");
    else if (!s.startsWith(INVALID_ASSET)) words.push(`the '${clip(s, 20)}' entry`);
  }
  if (perAsset.length === 1) words.push(`the entry for '${clip(perAsset[0].slice(INVALID_ASSET.length), 30)}', which the API could not place`);
  else if (perAsset.length > 1) words.push(`${perAsset.length} entries for assets the API could not place`);
  return words;
}

export function reconciliationView(r: HealthResponse): ReconciliationView {
  const g = r.grid;
  const rec = g.reconciliation;
  if (!rec) {
    return {
      known: false,
      lines: ["UNKNOWN - the answer carries no reconciliation, so the page cannot show that its counts reconcile to the rows read"],
      contradictions: []
    };
  }
  const bs = rec.by_state;
  const b = r.band;
  const onAssets = g.assets.filter(a => a.tiles.length > 0).length;
  // the synthetic flow entries by_state counts, as the answer names them
  const synthetic = syntheticWords(rec.synthetic_entries);
  const split = measuredBadSplit(r);
  // rows of page-excluded monitors are read but never drawn (A7): they leave the check count, and the line says so
  const rowsPart = rec.excluded_rows > 0
    ? `${rec.store_rows} − ${rec.excluded_rows} from excluded monitors + ${rec.made_by_api} made by the health API`
    : `${rec.store_rows} + ${rec.made_by_api} made by the health API`;
  // R4 D2: per-pipeline readings the API moved off a NOT_APPLICABLE asset sit in grid.unplaced too - they are no checks
  const ev = typeof rec.unplaced_evidence === "number" ? rec.unplaced_evidence : 0;
  const lines = [
    `${rec.store_rows} store rows → ${plural(rec.check_tiles, "check")} (${rowsPart}) → ` +
      `${rec.landed} landed on ${plural(onAssets, "asset")} · ${rec.unplaced} unplaced` +
      `${rec.rerouted > 0 ? ` (${rec.rerouted} moved off a NOT APPLICABLE asset)` : ""}` +
      `${ev > 0 ? ` + ${plural(ev, "per-pipeline reading")} moved off a NOT APPLICABLE asset` : ""} · ${rec.invalid} invalid` +
      " (the store's own self-check is the STATE STORE line, not a check tile)",
    `${plural(rec.assets, "asset")}: ${rec.visible_assets} shown${synthetic.length > 0 ? ` + ${synthetic.join(" + ")}` : ""} - ` +
      `fine ${bs.fine} · measured BAD ${bs.problem} (= Problems ${split.problems} + Downstream ${split.downstream} + ` +
      `Control plane ${split.control} + Housekeeping ${split.housekeeping}) · recovered ${bs.recovered} · unknown ${bs.unknown} · ` +
      `not applicable ${bs.not_applicable} · ${rec.hidden_assets} retired hidden`
  ];
  const c: string[] = [];
  // C7: the split is computed from the entries; it must add up to the answer's own measured-BAD total and to the chips
  const parts = `Problems ${split.problems} + Downstream ${split.downstream} + Control plane ${split.control} + Housekeeping ${split.housekeeping}`;
  if (split.total !== bs.problem) c.push(`the reconciliation counts ${bs.problem} measured BAD, but the answer's entries split into ${split.total} (${parts})`);
  if (split.problems !== b.problems) c.push(`${plural(split.problems, "measured BAD data entry", "measured BAD data entries")} not downstream, but the Problems count says ${b.problems}`);
  if (split.control !== b.monitoring_problems) c.push(`${plural(split.control, "measured BAD control-plane entry", "measured BAD control-plane entries")}, but the Control plane count says ${b.monitoring_problems}`);
  if (typeof b.suppressed === "number" && split.downstream !== b.suppressed) {
    c.push(`${plural(split.downstream, "measured BAD entry", "measured BAD entries")} collapsed under a primary, but the Downstream count says ${b.suppressed}`);
  }
  if (split.housekeeping > b.housekeeping) c.push(`${split.housekeeping} measured BAD housekeeping entries, more than the Housekeeping count ${b.housekeeping}`);
  if (!rec.balanced) c.push(`the answer says its checks do not balance: ${rec.landed} landed + ${rec.unplaced} unplaced against ${rec.check_tiles} checks`);
  if (rec.landed + rec.unplaced !== rec.check_tiles) {
    c.push(`${rec.landed} landed + ${rec.unplaced} unplaced = ${rec.landed + rec.unplaced}, not the ${rec.check_tiles} checks${rec.balanced ? " - yet the answer calls itself balanced" : ""}`);
  }
  const rowsSum = rec.store_rows - rec.excluded_rows + rec.made_by_api;
  if (rowsSum !== rec.check_tiles) {
    c.push(`${rec.store_rows} store rows${rec.excluded_rows > 0 ? ` - ${rec.excluded_rows} excluded` : ""} + ${rec.made_by_api} made by the API = ${rowsSum}, not ${rec.check_tiles} checks`);
  }
  for (const s of syntheticProblems(r)) c.push(s);
  const listed = [...r.monitors, ...r.unregistered_monitors].reduce((s, m) => s + m.tiles.length, 0);
  if (listed !== rec.check_tiles) c.push(`the answer lists ${plural(listed, "check")} under its monitors, but reconciles ${rec.check_tiles}`);
  const landedListed = g.assets.reduce((s, a) => s + a.tiles.length, 0);
  if (landedListed !== rec.landed) c.push(`the assets carry ${plural(landedListed, "landed check")}, but the reconciliation says ${rec.landed}`);
  if (g.unplaced.length !== rec.unplaced + ev) {
    c.push(`the answer lists ${g.unplaced.length} unplaced, but the reconciliation says ${rec.unplaced}${ev > 0 ? ` + ${ev} moved per-pipeline reading${ev === 1 ? "" : "s"}` : ""}`);
  }
  const marked = g.unplaced.filter(t => typeof t.evidence_of === "string").length;
  if (marked !== ev) c.push(`the answer marks ${plural(marked, "unplaced item")} as per-pipeline evidence, but the reconciliation says ${ev}`);
  if (g.invalid.length !== rec.invalid) c.push(`the answer lists ${g.invalid.length} invalid, but the reconciliation says ${rec.invalid}`);
  const hidden = g.assets.filter(a => a.hidden).length;
  if (g.assets.length !== rec.assets) c.push(`the answer lists ${plural(g.assets.length, "asset")}, but the reconciliation says ${rec.assets}`);
  if (hidden !== rec.hidden_assets || g.assets.length - hidden !== rec.visible_assets) {
    c.push(`the answer hides ${hidden} and shows ${g.assets.length - hidden} assets, but the reconciliation says ${rec.hidden_assets} hidden and ${rec.visible_assets} shown`);
  }
  const byStateSum = bs.fine + bs.problem + bs.recovered + bs.unknown + bs.not_applicable;
  const nSyn = rec.synthetic_entries.length;
  if (byStateSum !== rec.visible_assets + nSyn) {
    c.push(`the states add up to ${byStateSum}, not the ${rec.visible_assets} shown assets${nSyn > 0 ? ` + ${nSyn} synthetic entr${nSyn === 1 ? "y" : "ies"}` : ""}`);
  }
  return { known: true, lines, contradictions: c };
}

export function byWorst<T extends StateFields>(xs: readonly T[]): T[] {
  return [...xs].sort((a, b) => KIND_RANK[look(a).kind] - KIND_RANK[look(b).kind]);
}

export function countKinds(xs: readonly StateFields[]): Record<Kind, number> {
  const c = NO_KINDS();
  for (const x of xs) c[look(x).kind] += 1;
  return c;
}

export function kindsText(c: Record<Kind, number>): string {
  const parts: string[] = [];
  if (c.problem) parts.push(`${c.problem} PROBLEM`);
  if (c.unknown) parts.push(`${c.unknown} UNKNOWN`);
  if (c.recovered) parts.push(`${c.recovered} RECOVERED`);
  if (c.info) parts.push(`${c.info} INFO`);
  if (c.na) parts.push(`${c.na} NOT APPLICABLE`);
  if (c.fine) parts.push(`${c.fine} OK`);
  return parts.length > 0 ? parts.join(" · ") : "none";
}

// ------------------------------------------------------------------------------------------- format --
/** "2026-09-10 14:02 UTC". Every time on the page is UTC and says so. */
export function fmtUtc(iso: string | null | undefined): string {
  if (!isTime(iso)) return "no time recorded";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

function fmtSpan(h: number): string {
  if (h < 1) return `${Math.max(0, Math.round(h * 60))}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

/** An age in hours exactly as the server computed it at evaluation time. */
export function fmtAgeH(h: number | null | undefined): string {
  if (typeof h !== "number" || !Number.isFinite(h)) return "age unknown";
  if (h < 0) return `${fmtSpan(-h)} in the future`;
  return `${fmtSpan(h)} old`;
}

/** Client-clock age: used only for the page's own timestamps (as of, page built, last fetch). */
export function fmtAgoMs(ms: number): string {
  if (!Number.isFinite(ms)) return "age unknown";
  if (ms < -SKEW_TOLERANCE_MS) return `${fmtSpan(-ms / 3_600_000)} in the future (clock skew)`;
  if (ms < 60_000) return "just now";
  return `${fmtSpan(ms / 3_600_000)} ago`;
}

export function fmtAgo(iso: string | null | undefined, nowMs: number): string {
  return isTime(iso) ? fmtAgoMs(nowMs - Date.parse(iso)) : "age unknown";
}

/** Hours from one server timestamp to another (an asset's age at evaluation; the API carries no asset age_h). */
export function hoursBetween(fromIso: string | null | undefined, toIso: string | null | undefined): number | null {
  if (!isTime(fromIso) || !isTime(toIso)) return null;
  return (Date.parse(toIso) - Date.parse(fromIso)) / 3_600_000;
}

/** A recorded fact's date as the model wrote it ("2026-09-12"); its absence is said. */
export function recordedDate(at: string | null | undefined): string {
  return typeof at === "string" && at.trim() !== "" ? at.trim().slice(0, 10) : "undated";
}

/** The coverage denominator: "2 of 15 read". Its absence is stated, never hidden. */
export function coverageText(c: HealthCoverage | null | undefined): string {
  const has = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
  if (!c || (!has(c.measured) && !has(c.expected))) return "scope not stated by the monitor";
  return `${has(c.measured) ? c.measured : "?"} of ${has(c.expected) ? c.expected : "?"} read`;
}

/** The reading, rendered verbatim (the server redacts; the page adds nothing). */
export function valueText(v: unknown): string {
  if (v === null || v === undefined) return "no reading";
  if (typeof v === "string") return v.trim() === "" ? "empty reading" : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v) ?? "unreadable reading";
  } catch {
    return "unreadable reading";
  }
}

export function shortSha(sha: string | null | undefined): string {
  if (!sha) return "no commit stamp";
  if (/^[0-9a-f]{7,40}$/i.test(sha)) return sha.slice(0, 7).toLowerCase();
  return sha === "unknown" ? "commit unknown" : "unrecognised commit stamp";
}

/** The server's failing-since, said plainly; its absence is said, never guessed. */
export function sinceText(utc: string | null | undefined, isFloor: boolean | null | undefined): string {
  if (!isTime(utc)) return "since: not known";
  return isFloor ? `at least since ${fmtUtc(utc)} - the first recorded run of this check` : `since ${fmtUtc(utc)}`;
}

/** A DOM id for an entry (ids carry characters an id selector would need escaped). */
export function anchorId(id: string): string {
  return `ph-entry-${id.replace(/[^A-Za-z0-9_-]/g, "_")}`;
}

// ------------------------------------------------------------------------------ which monitor a check is --
// Every tile of health-api/2 names its `monitor`. Each check the API LANDED on an asset (grid rule 1) is matched to one
// monitor tile of identical content (the content includes that `monitor` field), trying the tile's own named monitor
// first, then the monitors the asset is watched by; otherwise it is named after a monitor ONLY when exactly one monitor
// has an unused identical tile; otherwise it says "monitor not identified" - never a guessed name. A tile the answer
// names but no monitor carries is therefore "not identified", and a monitor tile no asset carries is an orphan (drawn).
// This decides which monitor a check is NAMED after and nothing else: WHERE a check is drawn is the API's attribution,
// verbatim.

/** Canonical JSON (sorted keys) so two tiles compare by content, whatever the key order. */
function canon(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canon).join(",")}]`;
  if (v !== null && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .map(k => `${JSON.stringify(k)}:${canon(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v) ?? "null";
}

export interface PlacedCheck {
  tile: HealthTile;
  /** The monitor the tile came from; null when the answer does not identify exactly one (said so on the tile). */
  monitor: HealthMonitor | null;
  /** R5 E3: a per-pipeline READING the API moved off a NOT_APPLICABLE asset (grid.unplaced, marked `evidence_of`) - no copy
   *  of a monitor tile; it is named after its own `monitor` when the answer carries that monitor ("reading of m/check") */
  reading?: boolean;
}

export interface GridPlacement {
  /** asset id -> the checks the API landed on it, in answer order, each with the monitor it came from */
  landed: Map<string, PlacedCheck[]>;
  /** grid.unplaced: checks the API landed on no asset */
  unplaced: PlacedCheck[];
  /** monitor tiles the answer carries that no asset and no unplaced entry carries - drawn with the unplaced, never dropped */
  orphans: PlacedCheck[];
}

export function placeGridChecks(r: HealthResponse): GridPlacement {
  const all = [...r.monitors, ...r.unregistered_monitors];
  const tileCanon = all.map(m => m.tiles.map(canon));
  const used = new Set<string>();
  const ref = (mi: number, ti: number) => `${mi}:${ti}`;
  type Slot = { tile: HealthTile; key: string; prefer: string[]; out: PlacedCheck };
  const slots: Slot[] = [];
  const landed = new Map<string, PlacedCheck[]>();
  for (const a of r.grid.assets) {
    const placed = a.tiles.map(tile => {
      const out: PlacedCheck = { tile, monitor: null };
      const own = typeof tile.monitor === "string" ? [tile.monitor] : [];
      slots.push({ tile, key: canon(tile), prefer: [...own, ...(Array.isArray(a.monitors) ? a.monitors : [])], out });
      return out;
    });
    landed.set(a.id, placed);
  }
  const monitorOf = new Map<string, HealthMonitor>();
  for (const m of all) if (!monitorOf.has(m.id)) monitorOf.set(m.id, m);
  const unplaced = r.grid.unplaced.map(tile => {
    const out: PlacedCheck = { tile, monitor: null };
    // R5 E3 (agent-28): a per-pipeline READING moved off a NOT_APPLICABLE asset is no copy of a monitor tile, so it is matched
    // to none (and uses none up): it is named after its own `monitor` when the answer carries that monitor, else "not
    // identified" - never a guess
    if (typeof tile.evidence_of === "string") {
      out.reading = true;
      out.monitor = typeof tile.monitor === "string" ? (monitorOf.get(tile.monitor) ?? null) : null;
      return out;
    }
    slots.push({ tile, key: canon(tile), prefer: typeof tile.monitor === "string" ? [tile.monitor] : [], out });
    return out;
  });
  // pass 1 - the asset's own monitors, in the order the answer lists them
  const pending: Slot[] = [];
  for (const s of slots) {
    let hit: { mi: number; ti: number } | null = null;
    for (const id of s.prefer) {
      all.forEach((m, mi) => {
        if (hit !== null || m.id !== id) return;
        const ti = tileCanon[mi].findIndex((k, i) => k === s.key && !used.has(ref(mi, i)));
        if (ti >= 0) hit = { mi, ti };
      });
      if (hit !== null) break;
    }
    const h = hit as { mi: number; ti: number } | null;
    if (h !== null) {
      used.add(ref(h.mi, h.ti));
      s.out.monitor = all[h.mi];
    } else pending.push(s);
  }
  // pass 2 - named only when exactly one monitor has an unused identical tile
  for (const s of pending) {
    const owners = new Set<number>();
    let first: { mi: number; ti: number } | null = null;
    tileCanon.forEach((ks, mi) =>
      ks.forEach((k, ti) => {
        if (k !== s.key || used.has(ref(mi, ti))) return;
        owners.add(mi);
        if (first === null) first = { mi, ti };
      })
    );
    const f = first as { mi: number; ti: number } | null;
    if (f !== null && owners.size === 1) {
      used.add(ref(f.mi, f.ti));
      s.out.monitor = all[f.mi];
    }
  }
  // a check the answer carries under a monitor but lands nowhere (the API should never do this) is still drawn
  const orphans: PlacedCheck[] = [];
  all.forEach((m, mi) => m.tiles.forEach((tile, ti) => { if (!used.has(ref(mi, ti))) orphans.push({ tile, monitor: m }); }));
  // ...unless a slot above holds an identical tile with no named monitor (then the drawn slot IS that check)
  const unnamed = slots.filter(s => s.out.monitor === null).map(s => s.key);
  const orphanOut = orphans.filter(o => {
    const i = unnamed.indexOf(canon(o.tile));
    if (i < 0) return true;
    unnamed.splice(i, 1);
    return false;
  });
  return { landed, unplaced, orphans: orphanOut };
}

/** A stable identity for a drawn check: its monitor (or "?" when the answer does not identify one) and its name. */
export function checkKey(from: HealthMonitor | null | undefined, tile: HealthTile, fallback = "?"): string {
  return `${from ? from.id : fallback}::${tile.check}`;
}

// ------------------------------------------------------------------------------------------ the grid --
export function assetById(r: HealthResponse): Map<string, HealthGridAsset> {
  return new Map(r.grid.assets.map(a => [a.id, a] as const));
}

/** An entry named in a band list, in words: an asset's label and environment, or the unplaced checks. */
export function entryLabel(byId: Map<string, HealthGridAsset>, id: string): string {
  if (id === "unplaced") return "unplaced checks";
  // DESIGN-R2 A6 / DESIGN-R3 C5: the synthetic entries for model grid entries the API could not use
  if (id === "invalid") return "grid entries the API could not use";
  if (id.startsWith("invalid:")) return `grid entry '${clip(id.slice("invalid:".length), 40)}', which the API could not place`;
  const a = byId.get(id);
  return a ? `${a.label} (${a.env})` : id;
}

/** Every VISIBLE asset downstream of `id` along grid.links, transitively, nearest first. */
export function downstreamOf(r: HealthResponse, id: string): HealthGridAsset[] {
  const byId = assetById(r);
  const next = new Map<string, string[]>();
  for (const l of r.grid.links) next.set(l.from, [...(next.get(l.from) ?? []), l.to]);
  const seen = new Set<string>([id]);
  const out: HealthGridAsset[] = [];
  let frontier = [id];
  while (frontier.length > 0) {
    const nxt: string[] = [];
    for (const f of frontier) {
      for (const t of next.get(f) ?? []) {
        if (seen.has(t)) continue;
        seen.add(t);
        nxt.push(t);
        const a = byId.get(t);
        if (a && !a.hidden) out.push(a);
      }
    }
    frontier = nxt;
  }
  return out;
}

/** The band's style. It maps the server's colour; green is shown only when nothing in the same answer contradicts it
 *  ("if anything is unknown it ... does not say 'all healthy'"). */
export function bandLook(r: HealthResponse, p?: GridPlacement): Look {
  const b = r.band;
  if (b.colour === "red") return { kind: "problem", word: "PROBLEM", contradiction: null };
  if (b.colour === "amber") return unknownLook(null);
  if (b.colour !== "green") {
    return unknownLook(`the server sent an unrecognised verdict colour '${clip(String(b.colour), 20)}' - shown as UNKNOWN`);
  }
  const why: string[] = [];
  if (b.problems > 0) why.push(plural(b.problems, "problem"));
  if (b.unknown > 0) why.push(`${b.unknown} UNKNOWN`);
  if (b.monitoring_problems > 0) why.push(plural(b.monitoring_problems, "control-plane problem"));
  // a failed store read renders EVERY entry UNKNOWN, so a green band beside one contradicts it
  if (!r.store.read_ok) why.push("an unreadable state store");
  const loose = p ? [...p.unplaced, ...p.orphans].map(x => look(x.tile).kind) : [];
  const lp = loose.filter(k => k === "problem").length;
  const lu = loose.filter(k => k === "unknown").length;
  if (lp > 0) why.push(`${plural(lp, "measured problem check")} on no asset`);
  if (lu > 0) why.push(`${plural(lu, "UNKNOWN check")} on no asset`);
  // "All 0 measured nodes are healthy" is not a measurement: green needs at least one asset measured fine.
  if (!r.grid.assets.some(a => !a.hidden && assetLook(a).kind === "fine")) why.push("no visible asset measured fine (the answer measured nothing)");
  if (why.length > 0) {
    return unknownLook(`the server marked this verdict green, but the same answer reports ${why.join(", ")} - the page does not show that as healthy`);
  }
  return { kind: "fine", word: "OK", contradiction: null };
}

/** The tab title (DESIGN D6): "<P> problem(s) · <R> recovered · <U> unknown · Platform health". */
export function titleText(r: HealthResponse, base: string): string {
  const b = r.band;
  return `${plural(b.problems, "problem")} · ${b.recovered} recovered · ${b.unknown} unknown · ${base}`;
}

// ---------------------------------------------------------------------------------------- the shell --
export interface BuildStamp {
  utc: string | null;
  sha: string | null;
}

/** data-build-utc / data-build-sha, stamped on <html> by the build (vite.config.ts buildStamp). */
export function readBuildStamp(root: HTMLElement = document.documentElement): BuildStamp {
  return { utc: root.dataset.buildUtc ?? null, sha: root.dataset.buildSha ?? null };
}

// ------------------------------------------------------------------------------------ the API answer --
export type Parsed = { ok: true; data: HealthResponse } | { ok: false; why: string };

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);
const isTileLike = (x: unknown): boolean => isObj(x) && typeof x.state === "string" && typeof x.check === "string";
const isStrList = (x: unknown): boolean => Array.isArray(x) && x.every(v => typeof v === "string");
const isLine = (x: unknown): boolean => isObj(x) && typeof x.word === "string" && typeof x.kind === "string" && Array.isArray(x.cites);
const isBands = (x: unknown): boolean => Array.isArray(x) && x.every(b => isObj(b) && typeof b.id === "string" && typeof b.label === "string");

function gridProblem(g: unknown): string | null {
  if (!isObj(g)) return "it carries no dashboard grid (an API older than this page)";
  if (g.version !== 1) return `its dashboard grid declares version '${clip(String(g.version), 20)}', not 1`;
  const df = g.dataflow;
  const cp = g.control;
  if (!isObj(df) || !isBands(df.bands) || !Array.isArray(df.columns) ||
      !df.columns.every(c => isObj(c) && typeof c.id === "string" && Array.isArray(c.segments))) {
    return "its dataflow plane is missing or malformed";
  }
  if (df.rail !== null && df.rail !== undefined && !(isObj(df.rail) && typeof df.rail.label === "string" && Array.isArray(df.rail.between) &&
      isStrList(df.rail.from) && Array.isArray(df.rail.to))) {
    return "its rail is malformed";
  }
  if (!isObj(cp) || !isBands(cp.bands) || !Array.isArray(cp.columns) || !cp.columns.every(c => isObj(c) && typeof c.id === "string")) {
    return "its control plane is missing or malformed";
  }
  if (!Array.isArray(g.assets) || !Array.isArray(g.links) || !Array.isArray(g.unplaced) || !Array.isArray(g.invalid)) {
    return "its dashboard grid lists are missing";
  }
  for (const a of g.assets as unknown[]) {
    if (!isObj(a) || typeof a.id !== "string" || typeof a.label !== "string" || typeof a.state !== "string" ||
        (a.plane !== "dataflow" && a.plane !== "control") || typeof a.band !== "string" || !isStrList(a.cols) ||
        !Array.isArray(a.tiles) || !a.tiles.every(isTileLike) || !isStrList(a.members) || !isStrList(a.monitors) ||
        !Array.isArray(a.facts) || !isObj(a.lines) || !["schedule", "ran", "complete", "moved"].every(k => isLine((a.lines as Record<string, unknown>)[k]))) {
      return "an asset in its dashboard grid is malformed";
    }
    // DESIGN-R2 B8: the env badge is one of three words; anything else is not guessed at
    if (!(GRID_ENVS as readonly unknown[]).includes(a.env)) {
      return `an asset in its dashboard grid names the environment '${clip(String(a.env), 20)}', not PROD, DEV or GLOBAL`;
    }
    // DESIGN-R2 A2: evidence, when the answer carries it, is a list of well-formed entries
    if (a.evidence !== undefined && !(Array.isArray(a.evidence) && a.evidence.every(isEvidence))) {
      return "an asset's evidence in its dashboard grid is malformed";
    }
  }
  for (const l of g.links as unknown[]) {
    if (!isObj(l) || typeof l.from !== "string" || typeof l.to !== "string") return "a link in its dashboard grid is malformed";
  }
  if (!(g.unplaced as unknown[]).every(t => isTileLike(t) && isOptText((t as Record<string, unknown>).unplaced_reason) &&
      isOptText((t as Record<string, unknown>).evidence_of))) {
    return "an unplaced check in its dashboard grid is malformed";
  }
  // DESIGN-R3 C5: `entry` (the flow entry the note counts under), when the answer carries it, is text
  if (!(g.invalid as unknown[]).every(x => isObj(x) && typeof x.why === "string" && (x.entry === undefined || typeof x.entry === "string"))) {
    return "an invalid-entry note in its dashboard grid is malformed";
  }
  if (g.reconciliation !== undefined && !isReconciliation(g.reconciliation)) return "its dashboard grid's reconciliation is malformed";
  return null;
}

const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const isOptText = (x: unknown): boolean => x === undefined || x === null || typeof x === "string";
const isEvidence = (x: unknown): boolean =>
  isObj(x) && typeof x.line === "string" && typeof x.word === "string" && (x.kind === "problem" || x.kind === "recovered" || x.kind === "unknown") &&
  isOptText(x.reason) && isStrList(x.cites);
const RECON_KEYS = ["store_rows", "excluded_rows", "made_by_api", "check_tiles", "landed", "unplaced", "rerouted", "invalid", "assets",
  "visible_assets", "hidden_assets"] as const;
const BY_STATE_KEYS = ["fine", "problem", "recovered", "unknown", "not_applicable"] as const;
function isReconciliation(x: unknown): boolean {
  return isObj(x) && RECON_KEYS.every(k => isNum(x[k])) && (x.unplaced_evidence === undefined || isNum(x.unplaced_evidence)) &&
    typeof x.balanced === "boolean" && isObj(x.by_state) &&
    BY_STATE_KEYS.every(k => isNum((x.by_state as Record<string, unknown>)[k])) && isStrList(x.synthetic_entries);
}

/** Shape check of the answer. Anything that does not match the contract becomes the full-page UNKNOWN banner
 *  (never a partial page that looks like "no problems"). It checks shape only; it re-evaluates nothing. */
export function parseHealthResponse(body: unknown): Parsed {
  const fail = (why: string): Parsed => ({ ok: false, why });
  if (!isObj(body)) return fail("the answer is not a JSON object");
  if (body.schema !== "health-api/2") return fail(`it declares schema '${clip(String(body.schema), 40)}', not health-api/2`);
  if (!isTime(body.evaluated_at_utc)) return fail("it carries no evaluation time");
  const store = body.store;
  if (!isObj(store) || !isTileLike(store.self) || !Array.isArray(store.entries) || !Array.isArray(store.excluded) || typeof store.read_ok !== "boolean") {
    return fail("its store section is missing or malformed");
  }
  const band = body.band;
  if (!isObj(band) || typeof band.headline !== "string" || typeof band.problems !== "number" || typeof band.unknown !== "number" || typeof band.colour !== "string") {
    return fail("its verdict band is missing or malformed");
  }
  // The six counts the page leads with. An answer without them (an API older than the page) is refused, not guessed at.
  if (typeof band.monitoring_problems !== "number" || typeof band.housekeeping !== "number" || !Array.isArray(band.monitoring) ||
      !Array.isArray(band.housekeeping_entries) || !Array.isArray(band.unknown_causes) ||
      !band.unknown_causes.every(c => isObj(c) && typeof c.key === "string" && typeof c.count === "number" && Array.isArray(c.entries))) {
    return fail("its verdict band carries no monitoring / housekeeping / unknown-cause counts (an API older than this page)");
  }
  if (typeof band.recovered !== "number" || !Array.isArray(band.recovered_entries) || typeof band.not_watched !== "number" ||
      !Array.isArray(band.primaries) || !Array.isArray(band.possibly_consequent)) {
    return fail("its verdict band carries no recovered / not-watched counts (an API older than this page)");
  }
  if (!isObj(body.model)) return fail("its model section is missing");
  for (const k of ["monitors", "unregistered_monitors", "nodes", "edges"] as const) {
    if (!Array.isArray(body[k])) return fail(`its '${k}' list is missing`);
  }
  const cov = body.coverage;
  if (!isObj(cov) || !Array.isArray(cov.uncovered_nodes) || !Array.isArray(cov.excluded_monitors) || typeof cov.monitors_on_page !== "number") {
    return fail("its coverage section is missing or malformed");
  }
  for (const m of [...(body.monitors as unknown[]), ...(body.unregistered_monitors as unknown[])]) {
    if (!isObj(m) || typeof m.id !== "string" || !Array.isArray(m.tiles) || !m.tiles.every(isTileLike)) {
      return fail("a monitor in it is malformed");
    }
  }
  const g = gridProblem(body.grid);
  if (g !== null) return fail(g);
  return { ok: true, data: body as unknown as HealthResponse };
}
